import { z } from "zod";

import { CLIENT_TYPE_NAMES, CLIENT_TYPES } from "./client";

/**
 * MÜVEKKİL LİSTESİ, SÜZGEÇLERİ ve SIRALAMASI (CLOS-169 `[116]`).
 *
 * ### ⚠ ARŞİV varsayılan görünümde YOK
 *
 * Kart: *"arşivli kayıtlar varsayılan görünümden ayrılır; AÇIK FİLTRE/permission olmadan
 * dönmez."* Arşivlemek bir müvekkili gündemden çıkarmak demek; varsayılan liste onu
 * göstermeye devam etseydi arşivleme hiçbir şey yapmamış olurdu. Arşivleme AKSİYONU
 * `[119]`'un (CLOS-172) işi — burada yalnız okuma tarafı var: durum kolonu olmadan liste
 * "arşivliyi ayır" sözünü hiçbir zaman tutamazdı.
 *
 * ### ⚠ Süzgeç SUNUCUDA
 *
 * İstemci "arşivlileri gizle" deseydi sunucu onları yine gönderirdi ve arşivlenmiş bir
 * müvekkilin varlığı ağ trafiğinden okunabilirdi. Kart: *"erişim dışı müvekkilin adı,
 * SAYACI, snippet'i veya VARLIĞI sızmaz."*
 */

/**
 * Müvekkil DURUMU. İki değer: gündemdeki kayıt ve arşive alınmış kayıt.
 *
 * ⚠ Kapalı küme, çünkü durum bir süzgeç değeri: serbest metin olsaydı kullanıcı uydurma
 * bir durum göndererek boş listeyi "hiç kayıt yok" sanır, hangi kaydın nerede olduğunu
 * bilemezdik.
 */
export const CLIENT_STATUSES = ["active", "archived"] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const CLIENT_STATUS_NAMES: Record<ClientStatus, string> = {
  active: "Aktif",
  archived: "Arşivde",
};

/**
 * SIRALANABİLİR alanlar — KAPALI küme.
 *
 * ⚠ Kart: *"doğrudan filtre manipülasyonu"* negatif testi istiyor. Serbest bir alan adı
 * kabul etseydik, kullanıcı sıralamayı listede hiç göstermediğimiz bir kolona (ör.
 * `national_id`) çevirip o kolonun içeriğini sayfa sayfa ikili aramayla sızdırabilirdi —
 * sıralama, veriyi göstermeden sıralayan bir yan kanaldır. Sözleşme listesiyle (`[084]`)
 * aynı karar.
 */
export const CLIENT_SORT_FIELDS = ["displayName", "createdAt", "updatedAt"] as const;
export type ClientSortField = (typeof CLIENT_SORT_FIELDS)[number];

export const CLIENT_SORT_FIELD_NAMES: Record<ClientSortField, string> = {
  displayName: "Ad",
  createdAt: "Oluşturulma",
  updatedAt: "Son değişiklik",
};

/**
 * Müvekkil listesi sayfa boyutunun TAVANI.
 *
 * ⚠ DIŞA AÇIK, çünkü tüketici ekranların da bilmesi gerekiyor: `IncomingServiceRequestsPage`
 * bir süre `limit=100` yolluyordu ve istek ÜRETİMDE HER ZAMAN 422 dönüyordu (CLOS-875 tur-2).
 * Sayıyı iki yerde ayrı yazmak, tavan değiştiğinde ekranı sessizce bozan bir kopya demekti.
 */
export const CLIENT_LIST_MAX_LIMIT = 50;

export const clientListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(CLIENT_LIST_MAX_LIMIT).default(20),
  /** Ad VEYA e-postada arama; kullanıcı hangisini hatırlıyorsa aynı kutuya yazıyor. */
  q: z.string().trim().max(200).optional(),
  clientType: z.enum(CLIENT_TYPES).optional(),
  /**
   * `status` verilmezse ARŞİV HARİÇ hepsi döner. Boş bırakmak "hepsini göster" olsaydı,
   * kullanıcı hiçbir şey seçmeden arşivi de görür ve kartın kuralı bir varsayılanla
   * delinirdi.
   */
  status: z.enum(CLIENT_STATUSES).optional(),
  /**
   * Kaydı AÇAN kullanıcı.
   *
   * ⚠ "Açan" ile "sorumlu" AYRI eksenler ve ikisi de duruyor: kaydı açan kişi çoğu zaman
   * sorumlusu değildir. Adı olduğu gibi kalıyor.
   */
  createdBy: z.uuid().optional(),
  /**
   * PORTFÖY SORUMLUSU (CLOS-247 `[135]`).
   *
   * ⚠ Bu süzgeç bir süre YOKTU ve sebebi kayıtlıydı: verisi henüz doğmamıştı, hiçbir şeyin
   * yazamadığı bir kolonu süzmek kullanıcıya hep boş sonuç veren ölü bir süzgeç
   * göstermek olurdu. Portföy atamaları (`client_portfolio_assignments`) geldiğine göre
   * eksen artık gerçek.
   *
   * ⚠ Süzgeç yalnız DARALTIR: sorumluluk kayıt erişimi vermiyor (kartın kriteri), yani
   * erişimi olmayan bir müvekkil bu süzgeçle görünür hâle GELMİYOR. Filtresiz sonuç neyse,
   * filtreli sonuç onun alt kümesi.
   *
   * ⚠ Yalnız AÇIK sorumluluk sayılıyor: kapanmış atama tarihsel kayıttır, "bugün kimin
   * masasında?" sorusunun cevabı değil.
   */
  assigneeUserId: z.uuid().optional(),

  sort: z.enum(CLIENT_SORT_FIELDS).default("displayName"),
  dir: z.enum(["asc", "desc"]).default("asc"),
});
export type ClientListQuery = z.infer<typeof clientListQuerySchema>;

/**
 * SÜZGEÇ SEÇENEKLERİ ve sayaçları.
 *
 * ⚠ Kart: *"erişim dışı müvekkilin ... SAYACI ... sızmaz."* Sayaçlar listeyle AYNI
 * koşullardan üretiliyor; sabit bir seçenek listesi gösterseydik hiçbir müvekkili
 * görmeyen bir kullanıcı bile büroda hangi türlerin kullanıldığını öğrenirdi.
 */
export type ClientFacet = {
  value: string;
  count: number;
  /** Görünen ad SUNUCUDAN: arayüz kendi kod→ad tablosunu tutsaydı iki gerçek kaynağı olurdu. */
  label: string;
};

export type ClientListFacets = {
  clientType: ClientFacet[];
  status: ClientFacet[];
};

/** Tür sayacının görünen adı — katalog paylaşılan tarafta, tek yerde. */
export function clientTypeLabel(value: string): string {
  return CLIENT_TYPE_NAMES[value as keyof typeof CLIENT_TYPE_NAMES] ?? value;
}

export function clientStatusLabel(value: string): string {
  return CLIENT_STATUS_NAMES[value as keyof typeof CLIENT_STATUS_NAMES] ?? value;
}

/**
 * MÜVEKKİL YAŞAM DÖNGÜSÜ POLİTİKASI (CLOS-172 `[119]`).
 *
 * ### ⚠ Kartın AÇIK KARARI: politika SÜRÜMLÜ
 *
 * *"Aktif ilişki/sözleşme etkisi nedeniyle uyarı veya ek onay gerektiren durumlar SÜRÜMLÜ
 * POLİTİKADIR."* Bu yüzden kural koda değil `config/clientLifecycle.json`'a yazılıyor ve
 * dosya bir `version` taşıyor: bir büro "aktif sözleşmesi olan müvekkil arşivlenemesin"
 * derse, değişen şey bir kod dalı değil bir politika sürümü olacak. Kod içine `if`
 * yazsaydık, kuralın ne zaman ve neden değiştiği hiçbir yerde görünmezdi.
 */
export const clientLifecyclePolicySchema = z.object({
  version: z.number().int().min(1),
  archive: z.object({
    /** Taşınacak/etkilenecek ilişki varsa kullanıcı onayı ŞART mı. */
    acknowledgeRequiredWhenImpact: z.boolean(),
    /**
     * Arşivlemeyi tamamen ENGELLEYEN ilişki sınıfları. Bugün boş: kart *"arşivleme tek
     * başına ... sessizce kapatmaz"* diyor, "yasaktır" demiyor. Bir büro engellemek isterse
     * buraya sınıf adı yazıyor.
     */
    blockingRelationClasses: z.array(z.string().min(1)),
  }),
  reactivate: z.object({
    /**
     * Yeniden aktifleştirme onay istiyor çünkü kartın kuralı sezgiye AYKIRI: eski erişimler,
     * hizmet ilişkileri ve temsil yetkileri GERİ GELMİYOR. Kullanıcı bunu okumadan
     * onaylarsa, kaydı "eski hâline döndürdüğünü" sanır.
     */
    acknowledgeRequired: z.boolean(),
  }),
});
export type ClientLifecyclePolicy = z.infer<typeof clientLifecyclePolicySchema>;

/** Arşiv/yeniden aktifleştirme önizlemesi — kartın *"güvenli etki özeti"* kriteri. */
export type ClientLifecyclePreview = {
  status: string;
  version: number;
  /** Etkilenecek ilişki sınıfları; kayıtlı adapter'lardan (ADR-0030). */
  impacts: { key: string; label: string; count: number }[];
  /** Kullanıcının okuduğunu işaretlemesi gerekiyor mu. */
  acknowledgeRequired: boolean;
  /** Engelleyen bir ilişki varsa işlem hiç sunulmuyor. */
  blockedBy: string[];
  warnings: string[];
};

export const clientLifecycleCommandSchema = z.strictObject({
  version: z.coerce.number().int().min(1),
  /** Etki özetini okuduğuna dair onay; politikaya göre zorunlu. */
  acknowledge: z.boolean().default(false),
});
export type ClientLifecycleCommandInput = z.infer<typeof clientLifecycleCommandSchema>;

export const CLIENT_ARCHIVE_ACK_MESSAGE = "Arşivlemenin etkilerini onaylamanız gerekiyor";
export const CLIENT_REACTIVATE_ACK_MESSAGE =
  "Yeniden aktifleştirmenin eski erişim ve ilişkileri geri getirmediğini onaylamanız gerekiyor";
export const CLIENT_ALREADY_IN_STATE_MESSAGE = "Müvekkil zaten bu durumda";
export const CLIENT_MERGED_LIFECYCLE_MESSAGE =
  "Birleştirilmiş müvekkil arşivlenemez veya yeniden aktifleştirilemez";

/**
 * MÜVEKKİL–SÖZLEŞME PORTFÖY İLİŞKİSİ (CLOS-173 `[120]`).
 *
 * ### ⚠ Sözleşme TARAFI değil
 *
 * Kart: *"CLOS-111 sözleşme tarafları ile müvekkil-portföy ilişkisi EŞ ANLAMLI DEĞİLDİR."*
 * Bir müvekkil, tarafı olmadığı bir sözleşmenin portföyünde bulunabilir; bir taraf,
 * müvekkil kaydı olmayan bir şirket olabilir. Ayrım API'de, veri modelinde ve ekran
 * metinlerinde birlikte korunuyor (kartın bağımlılık sınırı).
 */
export const linkClientContractSchema = z.strictObject({ contractId: z.uuid() });

/**
 * SÖZLEŞMENİN BAĞLI OLDUĞU MÜVEKKİL(LER) — ters yön (CLOS-824).
 *
 * ⚠ Aynı ilişkinin öbür ucu; yeni bir kavram DEĞİL. Kullanıcı sözleşmeyi açmışken "bu
 * kimin sözleşmesi" sorusunun cevabını görebilmeli ve oradan atayabilmeli — müvekkil
 * sayfasına gidip sözleşmeyi aramak zorunda kalmamalı.
 */
export type ContractClientRelation = {
  id: string;
  clientId: string;
  clientDisplayName: string;
  status: string;
  createdByName: string;
  createdAt: string;
};
export type LinkClientContractInput = z.infer<typeof linkClientContractSchema>;

export type ClientContractRelation = {
  id: string;
  contractId: string;
  /**
   * Sözleşme başlığı — YALNIZ erişimi olan kullanıcıya. Liste sorgusu erişimi zaten
   * süzüyor; ilişkiyi tek başına döndüren komut yanıtlarında `null` kalıyor çünkü orada
   * başlık hiçbir soruyu cevaplamıyor.
   */
  contractTitle: string | null;
  status: string;
  createdByName: string;
  createdAt: string;
  removedByName: string | null;
  removedAt: string | null;
};

export const CLIENT_CONTRACT_DUPLICATE_MESSAGE = "Bu sözleşme müvekkilin portföyünde zaten var";
export const CLIENT_CONTRACT_NOT_LINKED_MESSAGE = "Bu sözleşme müvekkilin portföyünde bulunmuyor";

/**
 * SPONSORLU MÜVEKKİL ALANI (CLOS-175 `[122]`).
 *
 * ⚠ Görünüm SPONSORUN gördüğü kadarını taşıyor: alanın kimliği, ne zaman ve kim tarafından
 * kurulduğu. Alanın İÇİNDEKİ hiçbir şey (kullanıcı sayısı, sözleşmeler, aktivite) burada
 * yok — kart: *"sponsor ödeme ilişkisi ... görme/yönetme hakkı sağlamaz."*
 */
export type SponsoredWorkspaceView = {
  id: string;
  clientId: string;
  workspaceTenantId: string;
  createdByName: string;
  createdAt: string;
};

/**
 * SPONSORUN ÇAPRAZ LİSTESİ (CLOS-1096).
 *
 * ### ⚠ Neden ayrı bir görünüm
 *
 * `SponsoredWorkspaceView` TEK bir müvekkilin alanını anlatıyor ve müvekkil kimliğini
 * taşıyor; sponsor listesinde kullanıcı müvekkilin ADINI görmek zorunda — kimlikle
 * gezilecek bir liste, listenin işini görmez.
 *
 * ### ⚠ İÇERİK YOK ve bu bir güvenlik sınırı
 *
 * Alanın sözleşmeleri, belgeleri ve iç notları bu görünüme GİRMİYOR. Sponsor ödeme yaptığı
 * için veri sahibi olmuyor (CLOS-175'in değişmez kuralı); listeye "kaç sözleşmesi var" gibi
 * bir sayaç eklemek bile müvekkilin iç durumunu sızdırırdı.
 *
 * ### ⚠ DURUM ALANI YOK — bilerek
 *
 * "Aktif / Askıda / Sona ermiş" modeli şemada yok ve CLOS-867'nin açık kararını bekliyor.
 * Uydurulmuş bir durum, olmayan bir yaşam döngüsünü varmış gibi gösterirdi.
 */
export type SponsoredWorkspaceListItem = {
  id: string;
  clientId: string;
  /** Müvekkilin ADI — liste kimlikle gezilmez. */
  clientName: string;
  workspaceTenantId: string;
  createdByName: string;
  createdAt: string;
  /**
   * ⚠ ÇAĞIRANIN KENDİ TEMSİLİ — alanın içeriği DEĞİL (ADR-0066 §16.5).
   *
   * Rapor §16.5: *"Müvekkil Alanına git"* CTA'sı yalnız kullanıcının AYRICA gerçek bir
   * temsil/erişimi varsa görünür. Bu bayrak o soruyu cevaplıyor: "BEN bu alana
   * girebiliyor muyum?" — "alanda ne var?" değil. §16.4 sınırı korunuyor: sponsorluk
   * bir erişim yolu olmadığı için bayrak alanın içinden değil, çağıranın kendi temsil
   * satırından hesaplanıyor ve iki farklı kullanıcı aynı satırda farklı değer görebilir.
   */
  viewerCanEnter: boolean;
};

export const SPONSORED_WORKSPACE_DUPLICATE_MESSAGE =
  "Bu müvekkil için zaten bir Müvekkil Alanı kurulmuş";

/**
 * MÜVEKKİL ALANI PROFİLİ (CLOS-176 `[123]`).
 *
 * ### ⚠ Kartın AÇIK KARARI: modüller ve limitler SÜRÜMLÜ PROJEKSİYONDAN
 *
 * *"Sponsorlu alanda etkin modül/feature ve görünür limitler sürümlü entitlement
 * projection'ından alınır; HARD-CODE EDİLMEZ."* Bu yüzden liste
 * `config/clientWorkspace.json`'dan geliyor ve dosya bir `version` taşıyor: bir modülün
 * açılması bir kod dalı değil, bir sürüm değişikliği.
 *
 * ### ⚠ Sınırlar VARSAYILAN OLARAK GÖRÜNMÜYOR
 *
 * `visibleLimits` boş: sponsorun sayısal hakları (koltuk, sözleşme, depolama) SPONSORUN
 * ticari verisi. Müvekkilin alanında göstermek, ödeme ilişkisini bir bilgi sızıntısına
 * çevirirdi. Bir büro isterse konfigürasyondan açıyor.
 */
export const clientWorkspaceConfigSchema = z.object({
  version: z.number().int().min(1),
  modules: z.array(
    z.object({
      key: z.string().min(1),
      label: z.string().min(1),
      enabled: z.boolean(),
      /** Dolu ise modül SPONSORUN hakkına bağlı; hak yoksa modül hiç görünmüyor. */
      requiresEntitlement: z.string().min(1).nullable(),
    }),
  ),
  visibleLimits: z.array(z.string().min(1)),
});
export type ClientWorkspaceConfig = z.infer<typeof clientWorkspaceConfigSchema>;

export type ClientWorkspaceProfile = {
  tenantId: string;
  name: string;
  /**
   * ⚠ Sponsor bilgisi YALNIZ AD. Kart: *"sponsor hukuk bürosu bilgisi ödeme/hizmet
   * bağlamında GÖSTERİLEBİLİR; sponsorun YÖNETİM veya VERİ ERİŞİMİ VARMIŞ GİBİ
   * SUNULMAZ."* Bu yüzden burada sponsorun kimliği, üyeleri ya da bir yönetim bağlantısı
   * yok — yalnız "bu alanın masrafını kim karşılıyor" sorusunun cevabı.
   */
  sponsorName: string;
  /** Alanın kullanabileceği bölümler — konfigürasyon + sponsorun hakları. */
  modules: { key: string; label: string }[];
  /** Müvekkile gösterilen sayısal sınırlar; varsayılan boş. */
  limits: { key: string; value: number | boolean | "unlimited" }[];
};

export const clientWorkspaceProfileUpdateSchema = z.strictObject({
  name: z.string().trim().min(1, "Alan adı zorunludur").max(120),
});
export type ClientWorkspaceProfileUpdate = z.infer<typeof clientWorkspaceProfileUpdateSchema>;

export const CLIENT_WORKSPACE_ONLY_MESSAGE =
  "Bu ekran yalnız Müvekkil Alanı çalışma alanında kullanılabilir";
